import type { MetricKey } from './analytics.js';
import type { LocalizedText } from './locales.js';

/**
 * PDF RAPORUNUN METİNLERİ — worker'ın bastığı belge (29 Eyl 2026, 13 dil).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `messages/*.json` değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Raporu `apps/worker` üretiyor ve worker web'in mesaj dosyalarını import
 * edemiyor (bildirim kitaplarıyla aynı duvar — `notification-copy.ts`).
 * Başlıklar eskiden sabit Türkçeydi: İngilizce panelden istenen rapor
 * "Kampanya / Durum / Hesap" ve ham metrik anahtarlarıyla (`cpa`, `roas`)
 * geliyordu.
 *
 * `{ tr, en }` elle yazılıyor; diğer on bir dil çeviri betiğinin
 * `TEXT_DICTIONARIES` sözlüğünden (`@mentorivo/shared/text-dictionary`).
 *
 * 🔴 Metrik ve durum adları web'deki `automation.metric.*` ve
 * `entityStatus.*` ile AYNI olmak zorunda — rapor panelde gördüğünüz
 * sütunun adını taşımalı. `apps/worker/src/export/export-copy.spec.ts` iki
 * kaynağı karakter karakter karşılaştırıyor.
 */
export const EXPORT_COPY = {
  title: { tr: 'Mentorivo raporu', en: 'Mentorivo report' },
  campaign: { tr: 'Kampanya', en: 'Campaign' },
  status: { tr: 'Durum', en: 'Status' },
  platform: { tr: 'Platform', en: 'Platform' },
  account: { tr: 'Hesap', en: 'Account' },
  /// `{count}` rakamla dolduruluyor.
  rows: { tr: '{count} satır', en: '{count} rows' },
} as const satisfies Record<string, LocalizedText>;

export const EXPORT_METRIC_LABELS: Record<MetricKey, LocalizedText> = {
  spend: { tr: 'Harcama', en: 'Spend' },
  impressions: { tr: 'Gösterim', en: 'Impressions' },
  clicks: { tr: 'Tıklama', en: 'Clicks' },
  conversions: { tr: 'Dönüşüm', en: 'Conversions' },
  revenue: { tr: 'Gelir', en: 'Revenue' },
  ctr: { tr: 'TO', en: 'CTR' },
  cpc: { tr: 'TBM', en: 'CPC' },
  cpm: { tr: 'BGBM', en: 'CPM' },
  cpa: { tr: 'EBM', en: 'CPA' },
  roas: { tr: 'ROAS', en: 'ROAS' },
  reach: { tr: 'Erişim', en: 'Reach' },
  frequency: { tr: 'Frekans', en: 'Frequency' },
  addToCart: { tr: 'Sepete ekleme', en: 'Add to cart' },
  addToCartValue: { tr: 'Sepete ekleme değeri', en: 'Add-to-cart value' },
  initiateCheckout: { tr: 'Ödemeye başlama', en: 'Checkout started' },
  initiateCheckoutValue: { tr: 'Ödemeye başlama değeri', en: 'Checkout started value' },
  purchases: { tr: 'Satın alma', en: 'Purchases' },
  purchaseValue: { tr: 'Satın alma değeri', en: 'Purchase value' },
  searchImpressionShare: { tr: 'Gösterim payı', en: 'Impression share' },
  searchTopImpressionShare: { tr: 'Üst gösterim payı', en: 'Top impression share' },
  searchAbsoluteTopImpressionShare: { tr: 'En üst gösterim payı', en: 'Absolute top impression share' },
  searchBudgetLostImpressionShare: {
    tr: 'Bütçe yüzünden kaybedilen pay',
    en: 'Impression share lost to budget',
  },
  searchRankLostImpressionShare: { tr: 'Sıra yüzünden kaybedilen pay', en: 'Impression share lost to rank' },
};

/** Kampanya durumları — web'deki `entityStatus.*`. Bilinmeyen durum ham basılıyor. */
export const EXPORT_STATUS_LABELS: Record<string, LocalizedText> = {
  ACTIVE: { tr: 'Yayında', en: 'Active' },
  PAUSED: { tr: 'Duraklatıldı', en: 'Paused' },
  DELETED: { tr: 'Kaldırıldı', en: 'Removed' },
  ARCHIVED: { tr: 'Arşivlendi', en: 'Archived' },
  PENDING: { tr: 'Beklemede', en: 'Pending' },
  DISAPPROVED: { tr: 'Reddedildi', en: 'Disapproved' },
  LIMITED: { tr: 'Sınırlı', en: 'Limited' },
  IN_REVIEW: { tr: 'İncelemede', en: 'In review' },
  LEARNING: { tr: 'Öğreniyor', en: 'Learning' },
};
